import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { createSubmission } from "../lib/api";

const Eyebrow = ({ children }) => (
  <div className="flex items-center gap-3 mb-6">
    <span className="h-px w-10 bg-medblue" />
    <span className="text-[10px] uppercase tracking-[0.28em] text-medblue">{children}</span>
  </div>
);

const FORMS = {
  student: {
    eyebrow: "For Students",
    title: "Join the archive.",
    desc: "Get access to new interviews and, soon, mentorship.",
    fields: [
      { name: "school", label: "School", placeholder: "Your school" },
      { name: "grade", label: "Grade", placeholder: "e.g. Junior, Freshman" },
      { name: "interests", label: "Healthcare interests", placeholder: "e.g. neurology, public health", textarea: false },
    ],
  },
  professional: {
    eyebrow: "Be Interviewed",
    title: "Share your story.",
    desc: "Physicians, researchers, founders — tell the next generation how you got here.",
    fields: [
      { name: "profession", label: "Profession", placeholder: "e.g. Cardiologist" },
      { name: "specialty", label: "Specialty", placeholder: "Optional" },
      { name: "organization", label: "Organization", placeholder: "Where you work" },
      { name: "availability", label: "Availability", placeholder: "e.g. weekday evenings" },
    ],
  },
  partner: {
    eyebrow: "Partner",
    title: "Bring us to your school.",
    desc: "For universities, high schools, and healthcare organizations.",
    fields: [
      { name: "organization", label: "Organization", placeholder: "Your organization" },
    ],
  },
};

function JoinForm({ kind, spec }) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({});

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createSubmission({ kind, ...form });
      toast.success("Thanks — we'll be in touch shortly.");
      setForm({});
      e.target.reset();
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="border border-rule p-8 bg-white hover:border-navy transition-colors" data-testid={`form-${kind}`}>
      <Eyebrow>{spec.eyebrow}</Eyebrow>
      <h2 className="font-serif text-2xl text-navy leading-tight">{spec.title}</h2>
      <p className="mt-3 text-sm text-navy/60 leading-relaxed">{spec.desc}</p>
      <div className="mt-8 space-y-3">
        <input required onChange={onChange("name")} placeholder="Full name" className="w-full border border-rule focus:border-navy outline-none px-3 py-3 text-sm" data-testid={`${kind}-name`} />
        <input required type="email" onChange={onChange("email")} placeholder="Email address" className="w-full border border-rule focus:border-navy outline-none px-3 py-3 text-sm" data-testid={`${kind}-email`} />
        {spec.fields.map((f) => (
          <input key={f.name} onChange={onChange(f.name)} placeholder={f.placeholder} className="w-full border border-rule focus:border-navy outline-none px-3 py-3 text-sm" data-testid={`${kind}-${f.name}`} />
        ))}
        <textarea rows={4} onChange={onChange("message")} placeholder="Tell us a little about yourself…" className="w-full border border-rule focus:border-navy outline-none px-3 py-3 text-sm leading-relaxed" data-testid={`${kind}-message`} />
        <button type="submit" disabled={submitting} className="btn-primary w-full justify-center mt-2" data-testid={`${kind}-submit`}>
          {submitting ? "Sending…" : <>Send <ArrowRight size={13} className="ml-2" /></>}
        </button>
      </div>
    </form>
  );
}

export default function Join() {
  return (
    <div data-testid="join-page" className="bg-white">
      <section className="border-b border-rule">
        <div className="container-wide py-20 lg:py-24 max-w-5xl">
          <Eyebrow>Get Involved</Eyebrow>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-[68px] leading-[1.03] tracking-tight text-navy">
            Students. Professionals. Partners.
          </h1>
          <p className="mt-8 text-lg text-navy/65 max-w-2xl leading-[1.75]">
            Whether you&rsquo;re starting out, established, or building something with us — drop us a note and we&rsquo;ll be in touch.
          </p>
        </div>
      </section>

      <section className="container-wide py-20 grid grid-cols-1 md:grid-cols-3 gap-6">
        {Object.entries(FORMS).map(([kind, spec]) => (
          <JoinForm key={kind} kind={kind} spec={spec} />
        ))}
      </section>
    </div>
  );
}
